import * as styles from "./DuplicateJobDialog.css";

import { type JSX, type RefObject, useEffect, useState } from "react";

import { Button } from "@frontend/components/atoms/Button";
import {
  Combobox,
  ComboboxContent,
  ComboboxInput,
  ComboboxItem,
} from "@frontend/components/atoms/Combobox";
import {
  DatePicker,
  DatePickerContent,
  DatePickerTrigger,
  DatePickerValue,
} from "@frontend/components/atoms/DatePicker";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@frontend/components/atoms/Dialog";
import {
  Field,
  FieldContent,
  FieldLabel,
} from "@frontend/components/atoms/Field";
import { useIntlLocale } from "@frontend/hooks/useIntlLocale";
import { Trans, useLingui } from "@lingui/react/macro";

import { jobApi } from "../services";
import type { Employee } from "../types";

const DIALOG_WIDTH = 520;

export type DuplicateJobDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  projectId: number;
  jobId: number;
  onDuplicated: (jobId: number) => void;
  originRef?: RefObject<DOMRect | null>;
};

export function DuplicateJobDialog({
  open,
  onOpenChange,
  projectId,
  jobId,
  onDuplicated,
  originRef,
}: DuplicateJobDialogProps): JSX.Element {
  const { t } = useLingui();
  const locale = useIntlLocale();

  const [workers, setWorkers] = useState<Employee[]>([]);
  const [worker, setWorker] = useState<Employee | null>(null);
  const [date, setDate] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      return;
    }

    const controller = new AbortController();

    void jobApi
      .fetchEmployees(false, { signal: controller.signal })
      .then(setWorkers)
      .catch(() => undefined);

    return () => controller.abort();
  }, [open]);

  const close = () => {
    setWorker(null);
    setDate(null);
    setError(null);
    onOpenChange(false);
  };

  const duplicate = async () => {
    setError(null);
    setSaving(true);

    try {
      const copy = await jobApi.duplicateJob(projectId, jobId, {
        startDate: date,
        workerId: worker?.id ?? null,
      });

      onDuplicated(copy.id);
      close();
    } catch (failure: unknown) {
      setError(failure instanceof Error ? failure.message : String(failure));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          close();
        }
      }}
      originRef={originRef}
    >
      <DialogContent size={DIALOG_WIDTH}>
        <DialogHeader>
          <DialogTitle>
            <Trans>Duplicate the work post</Trans>
          </DialogTitle>

          <DialogClose />
        </DialogHeader>

        <div className={styles.body}>
          <DialogDescription>
            <Trans>
              The work post is copied as it stands. For a replacement, name the
              day it takes over and the worker taking over.
            </Trans>
          </DialogDescription>

          <Field>
            <FieldLabel>{t`Effective date of the replacement`}</FieldLabel>

            <FieldContent>
              <DatePicker locale={locale} value={date} onValueChange={setDate}>
                <DatePickerTrigger>
                  <DatePickerValue placeholder={t`As the original`} />
                </DatePickerTrigger>

                <DatePickerContent />
              </DatePicker>
            </FieldContent>
          </Field>

          <Field>
            <FieldLabel>{t`Worker`}</FieldLabel>

            <FieldContent>
              <Combobox<Employee>
                items={workers}
                value={worker}
                itemToStringLabel={(item) => item?.label ?? ""}
                isItemEqualToValue={(a, b) => a.id === b.id}
                onValueChange={setWorker}
              >
                <ComboboxInput placeholder={t`As the original`} />

                <ComboboxContent empty={t`No match.`}>
                  {(item: Employee) => (
                    <ComboboxItem key={item.id} value={item}>
                      {item.label}
                    </ComboboxItem>
                  )}
                </ComboboxContent>
              </Combobox>
            </FieldContent>
          </Field>

          {error ? (
            <p className={styles.alert} role="alert">
              {error}
            </p>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="neutral" onClick={close}>
            <Trans>Cancel</Trans>
          </Button>

          <Button
            variant="primary"
            loading={saving}
            onClick={() => void duplicate()}
          >
            <Trans>Duplicate</Trans>
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
